import { Injectable } from '@angular/core';
import { Allergens, Labels } from 'src/interfaces/enums.model';
import { DataService } from './data.service';
import { DESKTOP_TRANSLATIONS } from '../desktop/desktop-translations';

@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  constructor(private dataService: DataService) {}

  private get language(): string {
    return this.dataService.language;
  }

  /**
   * Возвращает локализованное значение поля.
   * @param item Объект с языковыми полями.
   * @param field Название поля (title, description и т.д.)
   */
  public getFieldForItem<T extends { languages?: any, [key: string]: any }>(
    item: T,
    field: string
  ): string {
    let langMap: Map<string, Map<string, string>> | undefined;
    // console.log(`LocalizationService | getFieldForItem() | ${this.language} | field: ${field}`, item);

    if (item.languages instanceof Map) {
      langMap = item.languages;
    } else if (item.languages && typeof item.languages === 'object') {
      langMap = new Map<string, Map<string, string>>();

      for (const [lang, fields] of Object.entries(item.languages)) {
        if (fields && typeof fields === 'object') {
          const innerMap = new Map<string, string>();

          for (const [key, value] of Object.entries(fields)) {
            if (value) innerMap.set(key, value);
          }

          if (innerMap.size > 0) {
            langMap.set(lang, innerMap);
          }
        }
      }
    }

    // console.log(`LocalizationService | getFieldForItem() | ${this.currentLang} | field: ${field}`, langMap);

    return (
      langMap?.get(this.language)?.get(field)?.trim() ||
      item[field]?.trim?.() || ''
    );
  }

  public titleTranslate<T extends { languages?: Map<string, string> | Record<string, string>, [key: string]: any }>(
    item: T,
  ): string {
    let map: Map<string, string> | undefined;

    if (item.languages instanceof Map) {
      map = item.languages;
    } else if (item.languages && typeof item.languages === 'object') {
      map = new Map(Object.entries(item.languages));
    }

    // console.log(`LocalizationService | titleTranslate() | ${this.currentLang}`, map);

    return (
      map?.get(this.language)?.trim() ||
      item['title']?.trim() ||
      ''
    );
  }

  private labelTranslations: Record<string, Record<Labels, string>> = {
    ru: {
      [Labels.premiere]: 'новинка',
      [Labels.hit]: 'хит',
      [Labels.spicy]: 'острое',
      [Labels.alcohol]: 'алкоголь',
      [Labels.pork]: 'свинина',
      [Labels.vegan]: 'вегетарианское',
      [Labels.protein]: 'High protein',
      [Labels.nonalcohol]: 'безалкогольное',
      [Labels.unknown]: '',
    },
    en: {
      [Labels.premiere]: 'new',
      [Labels.hit]: 'hit',
      [Labels.spicy]: 'spicy',
      [Labels.alcohol]: 'alcohol',
      [Labels.pork]: 'pork',
      [Labels.vegan]: 'vegetarian',
      [Labels.protein]: 'High protein',
      [Labels.nonalcohol]: 'non-alcoholic',
      [Labels.unknown]: '',
    },
    de: {
      [Labels.premiere]: 'neu',
      [Labels.hit]: 'hit',
      [Labels.spicy]: 'scharf',
      [Labels.alcohol]: 'alkoholisch',
      [Labels.pork]: 'schweinefleisch',
      [Labels.vegan]: 'vegetarisch',
      [Labels.protein]: 'High protein',
      [Labels.nonalcohol]: 'alkoholfrei',
      [Labels.unknown]: '',
    },
    fr: {
      [Labels.premiere]: 'nouveauté',
      [Labels.hit]: 'succès',
      [Labels.spicy]: 'épicé',
      [Labels.alcohol]: 'alcoolisé',
      [Labels.pork]: 'porc',
      [Labels.vegan]: 'végétarien',
      [Labels.protein]: 'High protein',
      [Labels.nonalcohol]: 'sans alcool',
      [Labels.unknown]: '',
    },
    sr: {
      [Labels.premiere]: 'novo',
      [Labels.hit]: 'hit',
      [Labels.spicy]: 'ljuto',
      [Labels.alcohol]: 'sa alkoholom',
      [Labels.pork]: 'svinjetina',
      [Labels.vegan]: 'vegetarijansko',
      [Labels.protein]: 'High protein',
      [Labels.nonalcohol]: 'bezalkoholno',
      [Labels.unknown]: '',
    },
    tr: {
      [Labels.premiere]: 'yeni',
      [Labels.hit]: 'hit',
      [Labels.spicy]: 'acı',
      [Labels.alcohol]: 'alkollü',
      [Labels.pork]: 'domuz eti',
      [Labels.vegan]: 'vejetaryen',
      [Labels.protein]: 'High protein',
      [Labels.nonalcohol]: 'alkolsüz',
      [Labels.unknown]: '',
    }
  };

  public getLabelTranslation(label: Labels): string {
    return this.labelTranslations[this.language]?.[label] ??
      this.labelTranslations['en']?.[label] ??
      '';
  }

  private allergenTranslations: Record<string, Record<Allergens, string>> = {
    ru: {
      [Allergens.gluten]: 'Глютен',
      [Allergens.crustaceans]: 'Ракообразные',
      [Allergens.eggs]: 'Яйца',
      [Allergens.fish]: 'Рыба',
      [Allergens.peanuts]: 'Арахис',
      [Allergens.soy]: 'Соя',
      [Allergens.milk]: 'Молоко',
      [Allergens.nuts]: 'Орехи',
      [Allergens.celery]: 'Сельдерей',
      [Allergens.mustard]: 'Горчица',
      [Allergens.sesame]: 'Кунжут',
      [Allergens.sulphites]: 'Сульфиты',
      [Allergens.lupin]: 'Люпин',
      [Allergens.molluscs]: 'Моллюски',
      [Allergens.unknown]: '',
    },
    en: {
      [Allergens.gluten]: 'Gluten',
      [Allergens.crustaceans]: 'Crustaceans',
      [Allergens.eggs]: 'Eggs',
      [Allergens.fish]: 'Fish',
      [Allergens.peanuts]: 'Peanuts',
      [Allergens.soy]: 'Soy',
      [Allergens.milk]: 'Milk',
      [Allergens.nuts]: 'Nuts',
      [Allergens.celery]: 'Celery',
      [Allergens.mustard]: 'Mustard',
      [Allergens.sesame]: 'Sesame',
      [Allergens.sulphites]: 'Sulphites',
      [Allergens.lupin]: 'Lupin',
      [Allergens.molluscs]: 'Molluscs',
      [Allergens.unknown]: '',
    },
    de: {
      [Allergens.gluten]: 'Gluten',
      [Allergens.crustaceans]: 'Krebstiere',
      [Allergens.eggs]: 'Eier',
      [Allergens.fish]: 'Fisch',
      [Allergens.peanuts]: 'Erdnüsse',
      [Allergens.soy]: 'Soja',
      [Allergens.milk]: 'Milch',
      [Allergens.nuts]: 'Schalenfrüchte',
      [Allergens.celery]: 'Sellerie',
      [Allergens.mustard]: 'Senf',
      [Allergens.sesame]: 'Sesam',
      [Allergens.sulphites]: 'Sulfite',
      [Allergens.lupin]: 'Lupinen',
      [Allergens.molluscs]: 'Weichtiere',
      [Allergens.unknown]: '',
    },
    fr: {
      [Allergens.gluten]: 'Gluten',
      [Allergens.crustaceans]: 'Crustacés',
      [Allergens.eggs]: 'Œufs',
      [Allergens.fish]: 'Poisson',
      [Allergens.peanuts]: 'Arachides',
      [Allergens.soy]: 'Soja',
      [Allergens.milk]: 'Lait',
      [Allergens.nuts]: 'Fruits à coque',
      [Allergens.celery]: 'Céleri',
      [Allergens.mustard]: 'Moutarde',
      [Allergens.sesame]: 'Sésame',
      [Allergens.sulphites]: 'Sulfites',
      [Allergens.lupin]: 'Lupin',
      [Allergens.molluscs]: 'Mollusques',
      [Allergens.unknown]: '',
    },
    sr: {
      [Allergens.gluten]: 'Gluten',
      [Allergens.crustaceans]: 'Rakovi',
      [Allergens.eggs]: 'Jaja',
      [Allergens.fish]: 'Riba',
      [Allergens.peanuts]: 'Kikiriki',
      [Allergens.soy]: 'Soja',
      [Allergens.milk]: 'Mleko',
      [Allergens.nuts]: 'Orašasti plodovi',
      [Allergens.celery]: 'Celer',
      [Allergens.mustard]: 'Senf',
      [Allergens.sesame]: 'Susam',
      [Allergens.sulphites]: 'Sulfiti',
      [Allergens.lupin]: 'Lupina',
      [Allergens.molluscs]: 'Mekušci',
      [Allergens.unknown]: '',
    },
    tr: {
      [Allergens.gluten]: 'Gluten',
      [Allergens.crustaceans]: 'Kabuklular',
      [Allergens.eggs]: 'Yumurta',
      [Allergens.fish]: 'Balık',
      [Allergens.peanuts]: 'Yer fıstığı',
      [Allergens.soy]: 'Soya',
      [Allergens.milk]: 'Süt',
      [Allergens.nuts]: 'Kuruyemiş',
      [Allergens.celery]: 'Kereviz',
      [Allergens.mustard]: 'Hardal',
      [Allergens.sesame]: 'Susam',
      [Allergens.sulphites]: 'Sülfitler',
      [Allergens.lupin]: 'Acı bakla',
      [Allergens.molluscs]: 'Yumuşakçalar',
      [Allergens.unknown]: '',
    },
  };

  public getAllergenTranslation(allergen: Allergens): string {
    return this.allergenTranslations[this.language]?.[allergen] ??
      this.allergenTranslations['en']?.[allergen] ??
      '';
  }

  private translations: Record<string, Record<string, string>> = {
    fats: {
      ru: 'Жиры',
      de: 'Fette',
      fr: 'Graisses',
      tr: 'Yağlar',
      sr: 'Masti',
      en: 'Fats'
    },
    protein: {
      ru: 'Белки',
      de: 'Proteine',
      fr: 'Protéines',
      tr: 'Protein',
      sr: 'Proteini',
      en: 'Protein'
    },
    carbs: {
      ru: 'Углеводы',
      de: 'Kohlenhydrate',
      fr: 'Glucides',
      tr: 'Karbonhidratlar',
      sr: 'Ugljeni hidrati',
      en: 'Carbs'
    },
    energy: {
      ru: 'Энергетическая ценность',
      de: 'Energiegehalt',
      fr: 'Valeur énergétique',
      tr: 'Enerji değeri',
      sr: 'Energetska vrednost',
      en: 'Energy'
    },
    drink: {
      ru: 'напиток',
      de: 'getränk',
      fr: 'boisson',
      tr: 'içecek',
      sr: 'piće',
      en: 'drink'
    },
    drinks: {
      ru: 'напитка',
      de: 'Getränk',
      fr: 'boisson',
      tr: 'içecek',
      sr: 'piće',
      en: 'drink'
    },
    food: {
      ru: 'блюдо',
      de: 'gericht',
      fr: 'plat',
      tr: 'yemek',
      sr: 'jelo',
      en: 'food'
    },
    foods: {
      ru: 'блюда',
      de: 'Gericht',
      fr: 'plat',
      tr: 'yemek',
      sr: 'jela',
      en: 'dish'
    },
    kkal: {
      ru: 'ккал',
      de: 'kcal',
      fr: 'kcal',
      tr: 'kkal',
      sr: 'kcal',
      en: 'kcal'
    },
    basket: {
      ru: 'Корзина',
      de: 'Warenkorb',
      fr: 'Panier',
      tr: 'Sepet',
      sr: 'Korpa',
      en: 'Basket'
    },
    order: {
      ru: 'Заказ',
      de: 'Bestellung',
      fr: 'Commande',
      tr: 'Sipariş',
      sr: 'Narudzbina',
      en: 'Order'
    },
    сontacts: {
      ru: 'Контакты',
      de: 'Kontakte',
      fr: 'Contacts',
      tr: 'Kontaklar',
      sr: 'Kontakti',
      en: 'Contacts'
    },
    ordersOnThisTable: {
      ru: 'Заказы на этом столе',
      de: 'Bestellungen auf diesem Tisch',
      fr: 'Commandes sur ce table',
      tr: 'Bu masa uzerinde siparişler',
      sr: 'Narudzbine na ovom stolu',
      en: 'Orders on this table'
    },
    wholeOrder: {
      ru: 'Весь счёт',
      de: 'Gesamtbestellung',
      fr: 'Commande totale',
      tr: 'Tüm sipariş',
      sr: 'Ukupna narudzbina',
      en: 'Whole order'
    },
    soldout: {
      ru: 'закончилось',
      de: 'ausverkauft',
      fr: 'épuisé',
      tr: 'tükendi',
      sr: 'rasprodato',
      en: 'sold out'
    },
    guest: {
      ru: "Гость",
      de: 'Gast',
      fr: 'Invite',
      tr: 'Misafir',
      sr: 'Gost',
      en: 'Guest'
    },
    total: {
      ru: 'Итого',
      de: 'Gesamt',
      fr: 'Total',
      tr: 'Toplam',
      sr: 'Ukupno',
      en: 'Total'
    },
    add: {
      ru: 'Добавить',
      de: 'Hinzufügen',
      fr: 'Ajouter',
      tr: 'Ekle',
      sr: 'Dodaj',
      en: 'Add'
    },
    added: {
      ru: 'добавки',
      de: 'hinzugefügt',
      fr: 'ajouté',
      tr: 'eklendi',
      sr: 'dodaci',
      en: 'added'
    },
    swipeToPlaceYourOrder: {
      ru: 'Сдвиньте, чтобы оформить заказ',
      de: 'Ziehen Sie, um zu bestellen',
      fr: 'Faites glisser pour placer votre commande',
      tr: 'Tıklayın ve siparişe yerleştirin',
      sr: 'Prevucite da pošaljete porudžbinu',
      en: 'Swipe to place your order'
    },
    slideToAddToOrder: {
      ru: 'Сдвиньте, чтобы добавить в заказ',
      de: 'Ziehen Sie, um zu bestellen',
      fr: 'Faites glisser pour ajouter au panier',
      tr: 'Tıklayın ve siparişe ekle',
      sr: 'Prevucite da dodate u porudžbinu',
      en: 'Swipe to add to order'
    },
    slideToProceedToCheckout: {
      ru: 'Сдвиньте, чтобы перейти к оплате',
      de: 'Schieben Sie, um zur Kasse zu gelangen',
      fr: 'Faites glisser pour procéder au paiement',
      tr: 'Ödeme işlemine geçmek için kaydırın',
      sr: 'Prevucite da pređete na plaćanje',
      en: 'Slide to proceed to checkout'
    },
    toPaySlideToTheRight: {
      ru: 'Сдвиньте, чтобы оплатить',
      de: 'Ziehen Sie, um zu bezahlen',
      fr: 'Faites glisser pour payer',
      tr: 'Tıklayın ve ödeme yapın',
      sr: 'Prevucite udesno da platite',
      en: 'Slide to the right to pay'
    },
    slideToCallWaiter: {
      ru: 'Сдвиньте, чтобы вызвать официанта',
      de: 'Ziehen Sie, um den Kellner zu rufen',
      fr: 'Faites glisser pour appeler le serveur',
      tr: 'Tıklayın ve servis arayın',
      sr: 'Prevucite da pozovete konobara',
      en: 'Slide to call waiter'
    },
    waiterIsAlreadyRushingToYou : {
      ru: "Официант уже спешит к вам",
      de: 'Der Kellner kommt schon zu Ihnen',
      fr: "Le serveur vient de vous appeler",
      tr: 'Servis zaten sizinle gidiyor',
      sr: 'Konobar već žuri ka vama',
      en: 'Waiter is already rushing to you'
    },
    callWaiterTitle: {
      ru: 'Вызвать официанта',
      de: 'Kellner rufen',
      fr: 'Appeler le serveur',
      tr: 'Garson çağır',
      sr: 'Pozovite konobara',
      en: 'Call a waiter',
    },
    callReasonWaiter: {
      ru: 'Позвать официанта',
      de: 'Kellner rufen',
      fr: 'Appeler un serveur',
      tr: 'Garson çağır',
      sr: 'Pozovi konobara',
      en: 'Call a waiter',
    },
    callReasonBill: {
      ru: 'Принести счёт',
      de: 'Rechnung bringen',
      fr: "Apporter l'addition",
      tr: 'Hesabı getir',
      sr: 'Donesite račun',
      en: 'Bring the bill',
    },
    callReasonHelp: {
      ru: 'Нужна помощь',
      de: 'Hilfe nötig',
      fr: "Besoin d'aide",
      tr: 'Yardım lazım',
      sr: 'Potrebna pomoć',
      en: 'Need help',
    },
    callSent: {
      ru: 'вызов отправлен',
      de: 'Ruf gesendet',
      fr: 'appel envoyé',
      tr: 'çağrı gönderildi',
      sr: 'poziv poslat',
      en: 'call sent',
    },
    cancelCall: {
      ru: 'Отменить',
      de: 'Abbrechen',
      fr: 'Annuler',
      tr: 'İptal et',
      sr: 'Otkaži',
      en: 'Cancel',
    },
    closeSheet: {
      ru: 'Отмена',
      de: 'Abbrechen',
      fr: 'Annuler',
      tr: 'Vazgeç',
      sr: 'Odustani',
      en: 'Cancel',
    },
    waiterOnTheWay: {
      ru: 'Официант идёт',
      de: 'Der Kellner kommt',
      fr: 'Le serveur arrive',
      tr: 'Garson geliyor',
      sr: 'Konobar dolazi',
      en: 'The waiter is on the way',
    },
    callAgainIn: {
      ru: 'Позвать снова можно через',
      de: 'Erneut rufen in',
      fr: 'Rappeler dans',
      tr: 'Tekrar çağırabilirsiniz',
      sr: 'Ponovo možete pozvati za',
      en: 'You can call again in',
    },
    callAgainThrough: {
      ru: 'ещё раз позвать через',
      de: 'noch einmal rufen',
      fr: 'appeler encore',
      tr: 'tekrar arayın',
      sr: 'ponovo pozovite za',
      en: 'call again through'
    },
    selectFoodAndOrDrinks: {
      ru: 'Выберите блюда и/или напитки',
      de: 'Lebensmittel und/oder Getränke auswählen',
      fr: 'Choisissez des aliments et/ou des boissons',
      tr: 'Yemek ve/veya içecekleri seçin',
      sr: 'Izaberite jela i/ili pića',
      en: 'Select food and/or drinks'
    },
    yourOrderIsAlreadyPlaced: {
      ru: 'Ваш заказ уже на кухне, и повара скоро начнут его готовить',
      de: 'Ihr Bestellung ist bereits auf der Kuche und der Koch wird bald darauf arbeiten',
      fr: 'Votre commande est deja sur la cuisine et le cuisinier va bientot la faire',
      tr: 'Siparişiniz hala küçükten geliyor ve hızlıca hazırlanacak',
      sr: 'Vaša porudžbina je već u kuhinji i uskoro će biti pripremljena',
      en: 'Your order is already on the kitchen and the cook will soon prepare it'
    },
    cancelOrder: {
      ru: 'Отменить заказ',
      de: 'Bestellung stornieren',
      fr: 'Annuler la commande',
      tr: 'Siparişi iptal et',
      sr: 'Otkaži porudžbinu',
      en: 'Cancel order'
    },
    cancelNewItems: {
      ru: 'Отменить новые позиции',
      de: 'Neue Positionen stornieren',
      fr: 'Annuler les nouveaux articles',
      tr: 'Yeni ürünleri iptal et',
      sr: 'Otkaži nove stavke',
      en: 'Cancel new items'
    },
    cancelOrderConfirm: {
      ru: 'Отменить ваш заказ? Официант его ещё не принял.',
      de: 'Ihre Bestellung stornieren? Die Bedienung hat sie noch nicht angenommen.',
      fr: 'Annuler votre commande ? Le serveur ne l’a pas encore acceptée.',
      tr: 'Siparişiniz iptal edilsin mi? Garson henüz kabul etmedi.',
      sr: 'Otkazati vašu porudžbinu? Konobar je još nije prihvatio.',
      en: 'Cancel your order? The waiter has not accepted it yet.'
    },
    cancelNewItemsConfirm: {
      ru: 'Отменить ваши новые позиции? Официант их ещё не принял.',
      de: 'Ihre neuen Positionen stornieren? Die Bedienung hat sie noch nicht angenommen.',
      fr: 'Annuler vos nouveaux articles ? Le serveur ne les a pas encore acceptés.',
      tr: 'Yeni ürünleriniz iptal edilsin mi? Garson henüz kabul etmedi.',
      sr: 'Otkazati vaše nove stavke? Konobar ih još nije prihvatio.',
      en: 'Cancel your new items? The waiter has not accepted them yet.'
    },
    yourOrderHasBeenSentToWaiters: {
      ru: 'Ваш заказ отправлен официанту, пожалуйста, подождите',
      de: 'Ihre Bestellung wurde an den Kellner weitergeleitet, bitte warten Sie.',
      fr: 'Votre commande a été envoyée au serveur, veuillez patienter.',
      tr: 'Siparişiniz garsona iletildi, lütfen bekleyiniz.',
      sr: 'Vaša porudžbina je poslata konobaru, sačekajte.',
      en: 'Your order has been sent to the waiter, please wait.'
    },
    table: {
      ru: 'Стол',
      de: 'Tisch',
      fr: 'Table',
      tr: 'Masa',
      sr: 'Stol',
      en: 'Table'
    },
    yourOrder: {
      ru: 'Ваш заказ',
      de: 'Ihr Bestellung',
      fr: 'Votre commande',
      tr: 'Siparişiniz',
      sr: 'Vaša porudžbina',
      en: 'Your order'
    },
    // Согласие под меню: текст + ссылка privacyPolicyLink (legal.edim.app)
    // Аккаунт заведения удалён (ROADMAP 1)
    menuUnavailable: {
      ru: 'Меню недоступно',
      de: 'Speisekarte nicht verfügbar',
      fr: 'Menu indisponible',
      tr: 'Menü kullanılamıyor',
      sr: 'Meni nije dostupan',
      en: 'Menu unavailable'
    },
    menuUnavailableText: {
      ru: 'Заведение больше не принимает заказы через Edim.',
      de: 'Das Lokal nimmt über Edim keine Bestellungen mehr an.',
      fr: 'L’établissement ne prend plus de commandes via Edim.',
      tr: 'İşletme artık Edim üzerinden sipariş almıyor.',
      sr: 'Objekat više ne prima porudžbine preko Edim-a.',
      en: 'The venue no longer takes orders through Edim.'
    },
    // Слайдер в конце меню промо-заведения (ROADMAP 5a) — регистрация владельца
    slideToConnectEdim: {
      ru: 'Это ваше заведение? Свайпните, если да',
      de: 'Ihr Lokal? Wischen Sie, wenn ja',
      fr: 'Votre établissement ? Glissez si oui',
      tr: 'İşletmeniz mi? Evetse kaydırın',
      sr: 'Vaš objekat? Prevucite ako jeste',
      en: 'Your venue? Swipe if yes'
    },
    iReadAndAgree: {
      ru: 'Я ознакомлен(-а) и согласен(-а) с',
      de: 'Ich habe gelesen und akzeptiere die',
      fr: 'J’ai lu et j’accepte la',
      tr: 'Okudum ve kabul ediyorum:',
      sr: 'Pročitao/la sam i prihvatam',
      en: 'I have read and agree to the'
    },
    privacyPolicyLink: {
      ru: 'политикой конфиденциальности',
      de: 'Datenschutzerklärung',
      fr: 'politique de confidentialité',
      tr: 'Gizlilik Politikası',
      sr: 'Politiku privatnosti',
      en: 'Privacy Policy'
    },
    notSpecified: {
      ru: 'Не указано',
      de: 'Nicht angegeben',
      fr: 'Non spécifié',
      tr: 'Belirtilmemis',
      sr: 'Nije određeno',
      en: 'Not specified'
    },
    remove: {
      ru: 'Убрать',
      de: 'Entfernen',
      fr: 'Retirer',
      tr: 'Kaldır',
      sr: 'Ukloni',
      en: 'Remove'
    },
    tips: {
      ru: 'Чаевые',
      de: 'Gebühren',
      fr: 'Frais',
      tr: 'Fiyatlar',
      sr: 'Bakšiš',
      en: 'Tips'
    },
    waiter: {
      ru: 'Официант',
      de: 'Kellner',
      fr: 'Serveur',
      tr: 'Garson',
      sr: 'Konobar',
      en: 'Waiter'
    },
    update: {
      ru: 'Обновить',
      de: 'Aktualisieren',
      fr: 'Actualiser',
      tr: 'Yenile',
      sr: 'Osveži',
      en: 'Refresh'
    },
    payForEverything: {
      ru: 'Оплатить всё',
      de: 'Alles bezahlen',
      fr: 'Payer tout',
      tr: 'Hepsini öde',
      sr: 'Platite sve',
      en: 'Pay for everything'
    },
    splitTheBill: {
      ru: 'Разделить счёт',
      de: 'Rechnung teilen',
      fr: 'Diviser la facture',
      tr: 'Borcu bölmek',
      sr: 'Podelite račun',
      en: 'Split the bill'
    },
    together: {
      ru: 'Популярно вместе',
      de: 'Beliebt zusammen',
      fr: 'Populaire ensemble',
      tr: 'Birlikte popüler',
      sr: 'Popularno zajedno',
      en: 'Popular together'
    },
    and: {
      ru: 'и',
      de: 'und',
      fr: 'et',
      tr: 've',
      sr: 'i',
      en: 'and'
    },
    from: {
      ru: 'от',
      de: 'von',
      fr: 'de',
      tr: '',
      sr: 'od',
      en: 'fr.'
    },
    // Поиск по меню
    searchMenu: {
      ru: 'Поиск по меню',
      de: 'Im Menü suchen',
      fr: 'Rechercher dans le menu',
      tr: 'Menüde ara',
      sr: 'Pretraga menija',
      en: 'Search the menu'
    },
    searchHint: {
      ru: 'Название блюда или ингредиент — например, «сыр» или «латте»',
      de: 'Name eines Gerichts oder eine Zutat – z. B. „Käse“ oder „Latte“',
      fr: 'Nom d\'un plat ou ingrédient — par exemple « fromage » ou « latte »',
      tr: 'Yemek adı veya malzeme — örneğin «peynir» ya da «latte»',
      sr: 'Naziv jela ili sastojak — na primer „sir“ ili „latte“',
      en: 'Dish name or ingredient — e.g. “cheese” or “latte”'
    },
    cancel: {
      ru: 'Отменить',
      de: 'Abbrechen',
      fr: 'Annuler',
      tr: 'İptal',
      sr: 'Otkaži',
      en: 'Cancel'
    },
    found: {
      ru: 'Найдено',
      de: 'Gefunden',
      fr: 'Trouvé',
      tr: 'Bulunan',
      sr: 'Pronađeno',
      en: 'Found'
    },
    nothingFound: {
      ru: 'Ничего не нашлось по запросу',
      de: 'Keine Treffer für',
      fr: 'Aucun résultat pour',
      tr: 'Sonuç bulunamadı:',
      sr: 'Nema rezultata za',
      en: 'Nothing found for'
    },
    nothingFoundHint: {
      ru: 'Попробуйте другое слово или посмотрите всё меню',
      de: 'Versuchen Sie ein anderes Wort oder sehen Sie sich das ganze Menü an',
      fr: 'Essayez un autre mot ou consultez tout le menu',
      tr: 'Başka bir kelime deneyin veya tüm menüye bakın',
      sr: 'Pokušajte drugu reč ili pogledajte ceo meni',
      en: 'Try another word or browse the whole menu'
    },
    allMenu: {
      ru: 'Всё меню',
      de: 'Ganzes Menü',
      fr: 'Tout le menu',
      tr: 'Tüm menü',
      sr: 'Ceo meni',
      en: 'Whole menu'
    },
    // Аллергены (Регламент (ЕС) № 1169/2011, Приложение II) — как в приложении Edim
    'allergens': {
      ru: 'Аллергены',
      de: 'Allergene',
      fr: 'Allergènes',
      tr: 'Alerjenler',
      sr: 'Alergeni',
      en: 'Allergens'
    },
    'allergensInAddons': {
      ru: 'Аллергены в добавках',
      de: 'Allergene in den Beilagen',
      fr: 'Allergènes dans les suppléments',
      tr: 'Ekstralardaki alerjenler',
      sr: 'Alergeni u dodacima',
      en: 'Allergens in add-ons'
    },
    'allergyNotice': {
      ru: 'Есть аллергия или непереносимость? Сообщите официанту перед заказом.',
      de: 'Allergie oder Unverträglichkeit? Bitte informieren Sie das Personal vor der Bestellung.',
      fr: 'Allergie ou intolérance ? Prévenez le serveur avant de commander.',
      tr: 'Alerjiniz veya intoleransınız mı var? Sipariş vermeden önce garsona bildirin.',
      sr: 'Imate alergiju ili intoleranciju? Recite konobaru pre porudžbine.',
      en: 'Allergy or intolerance? Please tell your server before ordering.'
    },
    ...DESKTOP_TRANSLATIONS,
  };

  public translate(key: string): string {
    const lang = document.documentElement.lang || 'en';
    const translation = this.translations[key];

    if (translation) {
      return translation[lang] || translation['en'] || key;
    }

    return key;
  }
}